import { useFlags } from "launchdarkly-react-client-sdk";

import { useClassicNavPreference } from "src/components/layout/nav/use-classic-nav";
import { useAiFeatureSettings } from "src/hooks/use-ai-feature-settings";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useIsAdStudioEnabled } from "src/pages/agents/hooks/use-is-ad-studio-enabled";
import {
  PROJECTS_AREA_SEGMENTS,
  PROJECTS_SETTINGS_SEGMENT,
} from "src/pages/projects/constants";
import { useLocation } from "src/router";

/** AMP v2 grants project management independently of the legacy Ad Studio grant. */
export const useProjectsAccess = () => {
  const { appAmpV2 } = useFlags();
  const { agents } = useAiFeatureSettings();
  const isAdStudioEnabled = useIsAdStudioEnabled();
  const { isPermitted: canViewAgents, isLoading } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_view_agents" },
  });
  return {
    isEnabled:
      ((Boolean(appAmpV2) && agents) || isAdStudioEnabled) && canViewAgents,
    isLoading,
  };
};

/** True inside the project-management area, where its nav replaces the main nav. */
export const useIsInProjects = () => {
  const { pathname } = useLocation();
  return isProjectsAreaSegment(pathname.split("/")[2]);
};

const isProjectsAreaSegment = (segment: string | undefined) =>
  PROJECTS_AREA_SEGMENTS.some((areaSegment) => areaSegment === segment);

/** True where the area's nav replaces the main nav. Held while access loads, so a deep link doesn't flash the main nav first. */
export const useShowProjectsNav = () => {
  const isInProjects = useIsInProjects();
  const { isEnabled, isLoading } = useProjectsAccess();
  return isInProjects && (isEnabled || isLoading);
};

/** AMP users can have the product tabs, and switch back to the classic navigation from the user menu. */
export const useCanUseProductTabs = () => {
  const { isEnabled } = useProjectsAccess();
  const { appAmpV2 } = useFlags();
  return isEnabled && Boolean(appAmpV2);
};

/** AMP users get the product tabs, which move between AMP, the CDP, and settings, unless they chose the classic navigation. */
export const useHasProductTabs = () => {
  const canUseProductTabs = useCanUseProductTabs();
  const { isClassicNav } = useClassicNavPreference();
  return canUseProductTabs && !isClassicNav;
};

export type Product = "amp" | "cdp" | "settings";

/** Agent pages that sit under Settings with the product tabs; the rest of the agent pages sit under AMP. */
const AGENT_SETTINGS_SUBSECTIONS = new Set(["custom-agents", "settings"]);

/** The Ad Studio section, whose templates sit under Context with the product tabs and whose other pages sit under AMP. */
export const AD_STUDIO_SECTION = "ad-studio";
const AD_STUDIO_CONTEXT_SUBSECTIONS = new Set(["templates"]);

/** Whether a workspace pathname, slug included, is one of Context's pages: Context Hub, the template library, and the custom agents. */
export const isContextPath = (pathname: string) => {
  const [, , section, subsection] = pathname.split("/");
  return (
    section === "context-hub" ||
    (section === AD_STUDIO_SECTION &&
      AD_STUDIO_CONTEXT_SUBSECTIONS.has(subsection ?? "")) ||
    (section === "lifecycle-studio" && subsection === "templates") ||
    (section === "agents" && subsection === "custom-agents")
  );
};

/**
 * The product a workspace pathname, slug included, belongs to; AMP's settings, agent settings, and Context's pages count as settings.
 * Context shares the full-screen settings view, with its own sidebar.
 */
export const productForPath = (pathname: string): Product => {
  const [, , section, subsection] = pathname.split("/");
  if (
    section === "settings" ||
    section === "organization" ||
    section === PROJECTS_SETTINGS_SEGMENT ||
    (section === "agents" &&
      AGENT_SETTINGS_SUBSECTIONS.has(subsection ?? "")) ||
    isContextPath(pathname)
  ) {
    return "settings";
  }
  return isProjectsAreaSegment(section) ||
    section === "agents" ||
    section === AD_STUDIO_SECTION
    ? "amp"
    : "cdp";
};

/** The product tab lit for the current page. */
export const useActiveProduct = (): Product =>
  productForPath(useLocation().pathname);

/**
 * Settings open in a modal over the page for product-tab users, so a settings URL the app loads or routes to is handed to it.
 * Held while access loads, so a deep link doesn't flash the settings page in the main view first.
 */
export const useIsSettingsUrl = () => {
  const isOnSettingsPath = useActiveProduct() === "settings";
  const { isEnabled, isLoading } = useProjectsAccess();
  const { appAmpV2 } = useFlags();
  const { isClassicNav } = useClassicNavPreference();
  return (
    isOnSettingsPath &&
    Boolean(appAmpV2) &&
    !isClassicNav &&
    (isEnabled || isLoading)
  );
};

/** The area's own nav, shown only inside it, so its code loads with the area rather than in every page's nav. */
export const loadProjectsNavLinks = () =>
  import("src/components/layout/nav/sections/projects-nav-links");
